import type { ReactElement } from 'react'
import type { DashboardTimeWindow } from '@/lib/dashboardLayouts'
import type { WidgetSize, WidgetView } from '@/lib/dashboardWidgets'
import {
  ActivePatientsWidget,
  AtRiskWidget,
  CompletedInPeriodWidget,
  FormsAwaitingReviewWidget,
  NewPatientsWidget,
  PipelineWidget,
  ProceduresByTypeWidget,
  ThroughputWidget,
  TransplantsInProgressWidget,
  UpcomingProceduresWidget,
} from './widgets/TransplantWidgets'
import {
  CleaningDueWidget,
  KitBuildsWidget,
  LateStepsWidget,
  MesRunsWidget,
  OpenPickListsWidget,
  PickListShortagesWidget,
  StepsInProgressWidget,
} from './widgets/ProcessWidgets'
import {
  BelowMinimumWidget,
  CapacityByTemperatureWidget,
  ConsumptionWidget,
  FullestUnitsWidget,
  LotsOnHandWidget,
  PendingInventoryWidget,
  ProductsByTypeWidget,
  ProductsMadeWidget,
  ShelfLifeWidget,
  ShipmentsByPartnerWidget,
  ShipmentsWidget,
  StockByCategoryWidget,
  StorageCapacityWidget,
  TopConsumedWidget,
  WhereProductsAreWidget,
} from './widgets/InventoryWidgets'
import {
  AlertsWidget,
  AuditActivityWidget,
  CapaAgeingWidget,
  CapaStatusWidget,
  CapaTimeToCloseWidget,
  CapaTrendWidget,
  EquipmentAvailabilityWidget,
  EquipmentByTypeWidget,
  IntegrationFailuresWidget,
  IntegrationHealthWidget,
  MaintenanceDoneWidget,
  MaintenanceDueWidget,
  NotificationsByTypeWidget,
  OverdueCapaWidget,
  RecentAuditWidget,
  ReservationsTodayWidget,
  ResourceAvailabilityWidget,
  UpcomingMaintenanceWidget,
} from './widgets/QualityWidgets'

/** What every widget body receives: the dashboard's period, its own size, and how to draw it. */
export type WidgetBodyProps = { time: DashboardTimeWindow; size: WidgetSize; view: WidgetView }

/**
 * Which component renders which widget, keyed by catalog id.
 *
 * THE single source of truth for "is this widget built", the same rule as
 * Reports' `BUILT`: the catalog lists every widget before it exists, and
 * membership here is what decides whether it renders and whether the picker
 * offers it. Widgets land here in D2b–D5b.
 */
export const BUILT_WIDGETS: Record<string, (props: WidgetBodyProps) => ReactElement | null> = {
  // D2b — Transplant, Patients & Forms (z8nrz7eqyj)
  'transplant.in-progress': TransplantsInProgressWidget,
  'transplant.completed': CompletedInPeriodWidget,
  'transplant.pipeline': PipelineWidget,
  'transplant.throughput': ThroughputWidget,
  'transplant.by-type': ProceduresByTypeWidget,
  'transplant.at-risk': AtRiskWidget,
  'upcoming-procedure.next-days': UpcomingProceduresWidget,
  'patients.new': NewPatientsWidget,
  'patients.active': ActivePatientsWidget,
  'collection.awaiting-review': FormsAwaitingReviewWidget,

  // D3b — Processing, MES, Kits, Cleaning & Pick lists (z8nrz7eqyn)
  'processing.steps-in-progress': StepsInProgressWidget,
  'processing.overdue-steps': LateStepsWidget,
  'mes.runs-by-status': MesRunsWidget,
  'kit.builds-by-status': KitBuildsWidget,
  'clean.due': CleaningDueWidget,
  'pick-lists.open': OpenPickListsWidget,
  'pick-lists.shortages': PickListShortagesWidget,

  // D4b — Inventory, Storage, Products & Shipping (z8nrz7eqyq)
  'inventory.below-minimum': BelowMinimumWidget,
  'inventory.stock-by-category': StockByCategoryWidget,
  'inventory.expiring': ShelfLifeWidget,
  'inventory.consumption': ConsumptionWidget,
  'inventory.top-consumed': TopConsumedWidget,
  'inventory.lots-on-hand': LotsOnHandWidget,
  'pending-inventory.count': PendingInventoryWidget,
  'storage.capacity': StorageCapacityWidget,
  'storage.capacity-by-type': CapacityByTemperatureWidget,
  'storage.units': FullestUnitsWidget,
  'products.by-status': WhereProductsAreWidget,
  'products.released': ProductsMadeWidget,
  'products.by-catalog': ProductsByTypeWidget,
  'shipping.by-status': ShipmentsWidget,
  'shipping.by-partner': ShipmentsByPartnerWidget,

  // D5b — Equipment, Resources, CAPA, Notifications, Audit & Integrations (z8nrz7eqyt)
  'equipment.maintenance-due': MaintenanceDueWidget,
  'equipment.maintenance-list': UpcomingMaintenanceWidget,
  'equipment.maintenance-done': MaintenanceDoneWidget,
  'equipment.by-status': EquipmentAvailabilityWidget,
  'equipment.by-type': EquipmentByTypeWidget,
  'equipment.reservations-today': ReservationsTodayWidget,
  'resource.availability': ResourceAvailabilityWidget,
  'capa.open-vs-closed': CapaStatusWidget,
  'capa.overdue': OverdueCapaWidget,
  'capa.trend': CapaTrendWidget,
  'capa.ageing': CapaAgeingWidget,
  'capa.time-to-close': CapaTimeToCloseWidget,
  'notifications.alerts': AlertsWidget,
  'notifications.by-category': NotificationsByTypeWidget,
  'audit.recent': RecentAuditWidget,
  'audit.activity': AuditActivityWidget,
  'integration.failures': IntegrationHealthWidget,
  'integration.failure-list': IntegrationFailuresWidget,
}

export function isWidgetBuilt(id: string): boolean {
  return id in BUILT_WIDGETS
}
